import { GenericDialog } from '@AMIEWEB/Alerts/GenericDialog';
import { Cancel } from '@AMIEWEB/Common/CancelModal/Cancel';
import { CircularProgress, Grid, Typography } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { EditWorkExperience } from './components/EditWorkExperience';
import { makeWorkExperienceDialogueStyles } from './EditWorkExperienceDialogue.styles';
import _ from 'lodash';
import { useReadOnly } from 'oidc/userRoles';
import { useDispatch, useSelector } from 'react-redux';
import { editExperienceSelector, selectReferences } from '../../Profile/CandidateDetails.selector';
import { ExperienceEditType } from 'app/models/Candidate/CandidateProfile';
import { candidateDetailActions } from '../../Profile/CandidateDetails.redux';
import { useParams } from 'react-router-dom';
import { getWorkExperiencePayload } from './Helpers/FormDataHelper';
import { selectUser } from 'oidc/user.selectors';
import { missingField } from 'app/constants';
import { globalActions } from 'app/ApplicationRoot/Global.redux';
import { postCandidateWorkExperience } from 'app/services/CandidateServices/CandidateServices';
import { DevTool } from '@hookform/devtools';
import { CustomBackdrop } from '@AMIEWEB/Common/Backdrop/CustomBackdrop';
import { brandsSelector, specialtiesSelector } from 'store/redux-store/lookup/lookup.selectors';
import { ReferencesStatus } from 'app/enums/WorkHistory';
import { trackException } from 'app-insights/appInsightsTracking';
import countriesData from 'app/assets/jsons/Country.json';
import { yupResolver } from '@hookform/resolvers/yup';
import { workExperieneceFormValidator } from './FormResolver/EditWorkExperienceFormResolver';

enum ExcludedCountries {
  VIRGIN_ISLANDS = 213,
  PUERTO_RICO = 17,
}

enum CountryCodes {
  USA = 1,
  CANADA = 2,
}

const getCountryOpts = () =>
  countriesData.countries
    .filter(x => x.ID !== ExcludedCountries.PUERTO_RICO && x.ID !== ExcludedCountries.VIRGIN_ISLANDS)
    .map(e => ({
      label: e.Description,
      value: e.CountryDialingCode,
      id: e.ID,
    }));

const EditWorkExperienceDialog = () => {
  const editExperience = useSelector(editExperienceSelector);
  const totalReferences = useSelector(selectReferences);
  const [countryOptions] = useState(getCountryOpts());
  const specialtiesLookup = useSelector(specialtiesSelector);

  const { travelerId: candidateId, brandId } = useParams<{ travelerId: string; brandId: string }>();
  const user = useSelector(selectUser);
  const [isLoading, setLoading] = useState(false);

  const { t } = useTranslation();
  const dispatch = useDispatch();

  const shouldDisableEdit = useMemo(() => {
    const currentWorkHistoryRecord = totalReferences?.items.filter(
      item => item.workHistoryID === editExperience.rowData?.workHistoryId,
    );
    const isDisabled = currentWorkHistoryRecord?.some(currentItem =>
      [ReferencesStatus.VERIFIED, ReferencesStatus.APPROVED, ReferencesStatus.UNAPPROVED].includes(
        currentItem.statusID,
      ),
    );
    return isDisabled || editExperience.rowData?.placementID;
  }, [editExperience.rowData, totalReferences?.items]);

  const { classes } = makeWorkExperienceDialogueStyles();

  const formMethods = useForm({
    mode: 'all',
    defaultValues: editExperience?.workExperience,
    resolver: yupResolver(workExperieneceFormValidator()),
    context: { shouldDisableEdit },
  });

  const {
    handleSubmit,
    reset,
    control,
    formState: { dirtyFields, errors, touchedFields, isDirty },
    getValues,
  } = formMethods;

  const dialogTitle =
    editExperience?.type == ExperienceEditType.TimeOff
      ? t('candidate.dialogActions.TimeOff')
      : t('candidate.dialogActions.EditWorkExperience');
  const [cancel, setCancel] = useState<boolean>(false);
  const { readOnly } = useReadOnly();

  useEffect(() => {
    if (editExperience?.workExperience) {
      reset(editExperience?.workExperience);
    }
  }, [editExperience?.workExperience, reset]);

  useEffect(() => {
    return () => {
      dispatch(
        candidateDetailActions.setEditExperience({
          workExperience: undefined,
          isModalOpen: false,
          type: ExperienceEditType.None,
          timeOff: undefined,
          rowData: undefined,
        }),
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleWorkExperienceUpdate = async data => {
    setLoading(true);
    const payload = getWorkExperiencePayload(
      data,
      editExperience?.workExperience,
      candidateId,
      brandId,
      user,
      specialtiesLookup,
    );
    const response = await postCandidateWorkExperience(payload);
    if (response?.status == 200) {
      const missingComponent = (
        <Typography component="span" style={{ color: '#a3a2a2' }}>
          {missingField}
        </Typography>
      );
      dispatch(candidateDetailActions.updateWorkExperienceGrid({ response, row: payload, missingComponent }));
      dispatch(
        globalActions.setSnackBar({
          message: 'Work Experience details have been updated successfully',
          severity: 'success',
        }),
      );
      yesAction();
    } else {
      dispatch(
        globalActions.setSnackBar({
          message: t('common.failedLoad'),
          severity: 'error',
        }),
      );
    }
    setLoading(false);
  };

  const onSubmit = async data => {
    await handleWorkExperienceUpdate(data);
  };
  const onError = data => {
    trackException({ exception: new Error('Error editing work history'), properties: { data } });
  };
  const handleCancel = () => {
    if (!_.isEmpty(errors) || !_.isEmpty(dirtyFields) || !_.isEmpty(touchedFields)) setCancel(true);
    else yesAction();
  };

  const yesAction = () => {
    setCancel(false);
    dispatch(
      candidateDetailActions.setEditExperience({
        workExperience: undefined,
        timeOff: undefined,
        type: ExperienceEditType.None,
        isModalOpen: false,
        rowData: undefined,
      }),
    );
  };
  const noAction = () => {
    setCancel(false);
  };

  const isEmpty = (str: string | any) => {
    if (str === null || str === '' || str === undefined) return true;
    return false;
  };

  const enableSaveOnChanges = () => {
    const modifiedValues = Object.keys(dirtyFields).map(x => ({
      field: x,
      value: getValues(x),
    }));

    let isSaveDisabled = readOnly || !(isDirty && _.isEmpty(errors));

    modifiedValues.forEach(entry => {
      switch (entry.field) {
        case 'facilityName':
          if (!entry.value || entry.value == editExperience?.workExperience[entry.field]) {
            isSaveDisabled = true;
          }
          break;

        case 'discipline':
        case 'specialty':
          if (!entry.value || entry.value == editExperience?.workExperience[entry.field]) {
            isSaveDisabled = true;
          }
          break;

        case 'zipCode':
        case 'currentlyWorkingReasonForLeaving':
        case 'numberOfBedsInFacility':
        case 'unitFloorDepartment':
        case 'positionHeld':
        case 'travelCompany':
        case 'isSupervisor':
        case 'isTeleTherapy':
        case 'isTeachingHospital':
        case 'isLocalStaffingAgency':
        case 'epnotes':
          if (entry.value == editExperience?.workExperience[entry.field]) {
            isSaveDisabled = true;
          }
          break;

        case 'country':
          if (entry.value == editExperience?.workExperience[entry.field]) {
            isSaveDisabled = true;
          } else {
            if (entry.value) {
              const state = getValues('state');
              const city = getValues('city');
              if (entry.value.id == CountryCodes.USA) {
                if (!state && !city) {
                  isSaveDisabled = true;
                }
              } else if (entry.value.id == CountryCodes.CANADA && !state) {
                isSaveDisabled = true;
              }
            }
          }
          break;

        case 'state':
          if (entry.value == editExperience?.workExperience[entry.field]) {
            isSaveDisabled = true;
          } else {
            const country = getValues('country');
            if (!entry.value && (country?.id == CountryCodes.USA || country?.id == CountryCodes.CANADA)) {
              isSaveDisabled = true;
            }
          }
          break;

        case 'city':
          if (entry.value == editExperience?.workExperience[entry.field]) {
            isSaveDisabled = true;
          } else {
            const country = getValues('country');
            if (!entry.value && country?.id == CountryCodes.USA) {
              isSaveDisabled = true;
            }
          }
          break;

        case 'currentlyWorkingStartDate':
          if (!entry.value || entry.value == editExperience?.workExperience[entry.field]) {
            isSaveDisabled = true;
          }
          break;

        case 'currentlyWorkingEndDate':
          if (entry.value == editExperience?.workExperience[entry.field]) {
            isSaveDisabled = true;
          }
          break;
      }
    });

    // check for required fields
    if (!isSaveDisabled) {
      const country = getValues('country');
      if (country) {
        if (country.id === CountryCodes.USA) {
          const state = getValues('state');
          const city = getValues('city');
          isSaveDisabled = isEmpty(state) || isEmpty(city);
        } else if (country.id === CountryCodes.CANADA) {
          const state = getValues('state');
          isSaveDisabled = isEmpty(state);
        }
      }
    }

    return isSaveDisabled;
  };

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit, onError)}>
        {isLoading ? (
          <CircularProgress className={classes.MuiCircularProgressRoot} />
        ) : (
          <>
            <GenericDialog
              variant={'blue'}
              classes={{ paper: classes.dialogContainer }}
              open={editExperience?.isModalOpen ?? false}
              disablePortal
              disableEscapeKeyDown
              dialogTitleProps={{
                text: dialogTitle,
                closeButton: true,
                expandable: true,
              }}
              dialogActions={[
                {
                  onClick: handleCancel,
                  text: 'CANCEL',
                  variant: 'contained',
                  color: 'tertiary',
                },
                {
                  text: `${t('candidate.dialogActions.Save')}`,
                  variant: 'contained',
                  type: 'submit',
                  disabled: enableSaveOnChanges(),
                  tooltipProps: readOnly
                    ? {
                        tooltipContent: t('global.readOnlyTooltip'),
                      }
                    : undefined,
                },
              ]}
              dialogContentProps={{
                classes: { root: classes.dialogContent },
              }}
              onClose={handleCancel}
            >
              <Grid spacing={3}>
                <Grid item xs={12}>
                  <EditWorkExperience shouldDisableEdit={shouldDisableEdit} countriesData={countryOptions} />
                  <CustomBackdrop open={isLoading} />
                </Grid>
              </Grid>
            </GenericDialog>
            {cancel && <Cancel openDialog={cancel} handleConfirmAction={yesAction} handleCancelAction={noAction} />}
          </>
        )}
      </form>
    </FormProvider>
  );
};

export default EditWorkExperienceDialog;
